import * as React from 'react';
import { Component, Fragment } from 'react';
import { CallRowViewModel } from '../ViewModels/IndexContainerViewModels';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import CallIcon from '@mui/icons-material/Call';
import CallMadeIcon from '@mui/icons-material/CallMade';
import VoiceChatIcon from '@mui/icons-material/VoiceChat';
import CallReceived from '@mui/icons-material/CallReceived';
import NotesIcon from '@mui/icons-material/Forum';
import TagsIcon from '@mui/icons-material/LocalOffer';
import Tooltip from '@mui/material/Tooltip';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import CircleIcon from '@mui/icons-material/Circle';
import { secondsConversion, formatDate } from '../Utilities/UtilityMethods';
import { CallTableColumns } from '../ViewModels/CallTableViewModel';
import displayScorecardResult from '../Utilities/DisplayScorecardScore';
import QuickreplyIcon from '@mui/icons-material/Quickreply';
import TableContainer from '@mui/material/TableContainer';

const styles = {
    root: {
        backgroundImage: 'call',
        color: 'black',
    },
    callFailed: {
        color: '#ff00007d',
        paddingLeft: '3px',
        paddingRight: '3px',
    },
    callSucceeded: {
        color: '#00800094',
        paddingLeft: '3px',
        paddingRight: '3px',
    },
    linkClass: {
        backgroundColor: 'white',
        padding: '2px',
        paddingLeft: '5px',
        paddingRight: '5px',
    },
    tableCell: {
        padding: '4px 12px 4px 12px',
    }
}

interface IRecordingTableProps {
    rows: CallRowViewModel[];
    openMonitoring: (systemPK: string) => void;
    isRTM: boolean,
    isClient: boolean,
    isQA: boolean,
    isAdmin: boolean,
    isResqAnywhere: () => boolean,
    monitoring?: boolean,
    isClientAIEnabled: boolean,
    columns: CallTableColumns,
    hideTable?: boolean
}

interface IRecordingTableState {

    copied: boolean;
}

/* To export component with params without having to pass classes back in when called, use below syntax */
export const DecoratedRecordingTable = (
    class RecordingTable extends Component<IRecordingTableProps, IRecordingTableState> {
        constructor(props) {
            super(props);
            this.state = {
                copied: false,
            }
        }

        public render() {
            const classes = styles;

            const rows = this.props.rows.map(row => {
                let callClass;
                let iconElement;

                switch (row.type) {
                    case 'Manual Call': {
                        callClass = classes.callSucceeded;
                        iconElement = <CallIcon className="imageClass" />;
                        break;
                    }
                    case 'O/B Call': {
                        callClass = classes.callSucceeded;
                        iconElement = <CallMadeIcon className="imageClass" />;
                        break;
                    }
                    case 'O/B Call Failed': {
                        callClass = classes.callFailed;
                        iconElement = <CallMadeIcon className="imageClass" />;
                        break;
                    }
                    case 'I/B Call': {
                        callClass = classes.callSucceeded;
                        iconElement = <CallReceived className="imageClass" />;
                        break;

                    }
                    case 'Manual Call Failed': {
                        callClass = classes.callFailed;
                        iconElement = <CallIcon className="imageClass" />;
                        break;
                    }
                    case 'I/B Call Failed': {
                        callClass = classes.callFailed;
                        iconElement = <CallReceived className="imageClass" />;
                        break;
                    }//types of 'WebChat'
                    case 'Agent Closed Chat':  //by not adding a break it will include the code of the following case
                    case 'Customer Closed Chat':
                    case 'Transfer': {
                        callClass = classes.callSucceeded;
                        iconElement = <VoiceChatIcon className="imageClass" />;
                        break;
                    }
                    default: {
                        callClass = classes.root;
                        iconElement = <CallIcon className="imageClass" />;
                        break;
                    }

                }

                const tagIcon = (row.tagsFlag && (this.props.isQA || this.props.isAdmin) && !this.props.isClient &&
                    <>
                        <Tooltip title="Has Tags" placement="top">
                            <TagsIcon />
                        </Tooltip>
                    </>);

                const noteIcon = (row.notesFlag && !this.props.isClient &&
                    <>
                        <Tooltip title="Has Notes" placement="top">
                            <NotesIcon />
                        </Tooltip>
                    </>);

                const aINoteIcon = (!this.props.isClient && row.aiNotes.hasAINotes &&
                    <>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}>
                            <Tooltip title={`Has AI Generated Notes ${row.aiNotes.areNotesSaved ? ' (Saved)' : ''}`} placement="top">
                                <QuickreplyIcon sx={{ color: row.aiNotes.areNotesSaved ? 'blue' : 'darkorange' }} />
                            </Tooltip>
                        </div>
                    </>);

                let RTMInfo = <Fragment />;

                if (!this.props.isClient && this.props.rows.filter(f => f.result === 'NC').length !== 0) {
                    RTMInfo = (
                        <TableCell />
                    );

                    if (row.ansFlag) {
                        RTMInfo = (
                            <TableCell>
                                <p>ANS</p>
                            </TableCell>
                        );
                    } else if (row.ncFlag) {
                        RTMInfo = (
                            <TableCell>
                                <p>NC</p>
                            </TableCell>
                        );
                    } else if (row.result === 'NC') {
                        RTMInfo = (
                            <TableCell>
                                <p>Unmarked</p>
                            </TableCell>
                        );
                    }
                }

                return (
                    <TableRow key={row.systemPK} data-key={row.systemPK} onClick={this.openCallInfo}>
                        {!this.props.monitoring && this.props.columns.clientFilter.isActive && <TableCell component="th" scope="row" sx={styles.tableCell}>{row.client}</TableCell>}
                        {this.props.columns.agentNameFilter.isActive && <TableCell component="th" scope="row" sx={styles.tableCell}>{row.agentName}</TableCell>}
                        {this.props.columns.eventTimeFilter.isActive && <TableCell sx={styles.tableCell}>
                            {formatDate(row.eventTime)}
                            <br />
                            {new Date(row.eventTime).toLocaleTimeString()}
                        </TableCell>}
                        {this.props.columns.callLengthFilter.isActive && <TableCell sx={styles.tableCell}>{secondsConversion(row.callLength)}</TableCell>}
                        {!this.props.monitoring && this.props.columns.channelFilter.isActive && <TableCell sx={styles.tableCell}>{row.channel}</TableCell>}
                        {this.props.columns.typeFilter.isActive &&
                            <TableCell align="center" sx={styles.tableCell}>
                                <div style={callClass} title={row.type}>
                                    {iconElement}
                                </div>
                            </TableCell>
                        }
                        {this.props.columns.sentimentFilter.isActive &&
                            <TableCell sx={styles.tableCell} align="center">
                                {
                                    this.props.isClient && !this.props.isClientAIEnabled ?
                                        <Tooltip title="Unavailable" placement="top"><CircleIcon fontSize="medium" sx={{ color: `grey` }} /></Tooltip>
                                        :
                                        row.customerSentimentEnd !== undefined ?
                                            (row.customerSentimentEnd >= -0.5 && row.customerSentimentEnd <= 0.5) ?
                                                <SentimentNeutralIcon fontSize="medium" sx={{ color: `#cccc00` }} />
                                                : row.customerSentimentEnd < -0.5 ?
                                                    <SentimentVeryDissatisfiedIcon fontSize="medium" sx={{ color: `red` }} />
                                                    : row.customerSentimentEnd > 0.5 ?
                                                        <SentimentVerySatisfiedIcon fontSize="medium" sx={{ color: `green` }} />
                                                        : <Fragment />
                                            :
                                            <Fragment />
                                }
                            </TableCell>
                        }
                        {this.props.columns.resultFilter.isActive && <TableCell sx={styles.tableCell}>{row.result}</TableCell>}
                        {this.props.columns.phoneNumberFilter.isActive && <TableCell sx={styles.tableCell}>{row.telephone}</TableCell>}
                        {this.props.columns.teamLeaderFilter.isActive && <TableCell sx={styles.tableCell}>{row.teamLeader}</TableCell>}
                        {this.props.columns.teamFilter.isActive && <TableCell sx={styles.tableCell}>{row.team}</TableCell>}
                        {this.props.columns.reportCode1Filter.isActive && <TableCell sx={styles.tableCell}>{row.reportCode1}</TableCell>}
                        {this.props.columns.reportCode1DescriptionFilter.isActive && <TableCell sx={styles.tableCell}>{row.reportCode1Description}</TableCell>}
                        {this.props.columns.reportCode3Filter.isActive && <TableCell sx={styles.tableCell}>{row.reportCode3}</TableCell>}
                        {this.props.columns.reportCode3DescriptionFilter.isActive && <TableCell sx={styles.tableCell}>{row.reportCode3Description}</TableCell>}
                        {this.props.columns.siteFilter.isActive && <TableCell sx={styles.tableCell}>{row.site}</TableCell>}
                        {this.props.columns.systemIDFilter.isActive && <TableCell sx={styles.tableCell}>{row.systemID}</TableCell>}
                        {this.props.columns.payrollNumberFilter.isActive && <TableCell sx={styles.tableCell}>{row.payrollNumber}</TableCell>}
                        {this.props.columns.platformFilter.isActive && <TableCell sx={styles.tableCell}>{row.platform}</TableCell>}
                        {this.props.columns.callReasonFilter.isActive && <TableCell sx={styles.tableCell}>{row.callReason}</TableCell>}
                        {this.props.columns.callCategoryFilter.isActive && <TableCell sx={styles.tableCell}>{row.callCategory}</TableCell>}
                        {this.props.columns.complaintFilter.isActive && <TableCell sx={styles.tableCell}>{row.complaint}</TableCell>}
                        {this.props.columns.complaintCategoryFilter.isActive && <TableCell sx={styles.tableCell}>{row.complaintCategory}</TableCell>}
                        {this.props.columns.vulnerabilityCategoryFilter.isActive && <TableCell sx={styles.tableCell}>{row.vulnerabilityCategory}</TableCell>}
                        {this.props.columns.vulnerabilityFilter.isActive && <TableCell sx={styles.tableCell}>{row.vulnerability}</TableCell>}
                        {this.props.columns.scoreFilter.isActive && <TableCell sx={styles.tableCell}>{row.score !== null && displayScorecardResult(row.score)}</TableCell>}
                        {this.props.columns.saleTypeFilter.isActive && <TableCell sx={styles.tableCell}>{row.saleType}</TableCell>}
                        {this.props.columns.productFilter.isActive && <TableCell sx={styles.tableCell}>{row.product}</TableCell>}
                        {this.props.columns.productTypeFilter.isActive && <TableCell sx={styles.tableCell}>{row.productType}</TableCell>}
                        {this.props.columns.productOfferFilter.isActive && <TableCell sx={styles.tableCell}>{row.productOffer}</TableCell>}
                        {this.props.columns.clientReferenceFilter.isActive && <TableCell sx={styles.tableCell}>{row.clientReference}</TableCell>}
                        {this.props.columns.themeFilter.isActive && <TableCell sx={styles.tableCell}>{row.theme}</TableCell>}
                        {this.props.columns.originatorFilters.isActive && <TableCell sx={styles.tableCell}>{row.originator}</TableCell>}
                        {this.props.columns.outcomeFilters.isActive && <TableCell sx={styles.tableCell}>{row.outcome}</TableCell>}
                        {this.props.columns.customerIDFilter.isActive &&
                            <TableCell sx={styles.tableCell}>{
                                !this.props.monitoring &&
                                row.customerID
                            }
                            </TableCell>
                        }
                        <TableCell sx={styles.tableCell}>
                            {noteIcon}
                            {aINoteIcon}
                            {tagIcon}
                        </TableCell>
                        {RTMInfo}
                    </TableRow >
                );
            });

            return (
                <TableContainer style={{ height: '100%', overflowY: 'scroll' }} >
                    <Table stickyHeader >
                        <TableHead>
                            <TableRow>
                                {!this.props.monitoring && this.props.columns.clientFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.clientFilter.name}</TableCell>}
                                {this.props.columns.agentNameFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.agentNameFilter.name}</TableCell>}
                                {this.props.columns.eventTimeFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.eventTimeFilter.name}</TableCell>}
                                {this.props.columns.callLengthFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.callLengthFilter.name}</TableCell>}
                                {!this.props.monitoring && this.props.columns.channelFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.channelFilter.name}</TableCell>}
                                {this.props.columns.typeFilter.isActive && <TableCell sx={styles.tableCell} align="center">{this.props.columns.typeFilter.name}</TableCell>}
                                {this.props.columns.sentimentFilter.isActive && <Tooltip title="Customer's sentiment at end of call" placement="top">
                                    <TableCell sx={styles.tableCell} align="center">{this.props.columns.sentimentFilter.name}</TableCell>
                                </Tooltip>}
                                {this.props.columns.resultFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.resultFilter.name}</TableCell>}
                                {this.props.columns.phoneNumberFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.phoneNumberFilter.name}</TableCell>}
                                {this.props.columns.teamLeaderFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.teamLeaderFilter.name}</TableCell>}
                                {this.props.columns.teamFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.teamFilter.name}</TableCell>}
                                {this.props.columns.reportCode1Filter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.reportCode1Filter.name}</TableCell>}
                                {this.props.columns.reportCode1DescriptionFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.reportCode1DescriptionFilter.name}</TableCell>}
                                {this.props.columns.reportCode3Filter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.reportCode3Filter.name}</TableCell>}
                                {this.props.columns.reportCode3DescriptionFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.reportCode3DescriptionFilter.name}</TableCell>}
                                {this.props.columns.siteFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.siteFilter.name}</TableCell>}
                                {this.props.columns.systemIDFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.systemIDFilter.name}</TableCell>}
                                {this.props.columns.payrollNumberFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.payrollNumberFilter.name}</TableCell>}
                                {this.props.columns.platformFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.platformFilter.name}</TableCell>}
                                {this.props.columns.callReasonFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.callReasonFilter.name}</TableCell>}
                                {this.props.columns.callCategoryFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.callCategoryFilter.name}</TableCell>}
                                {this.props.columns.complaintFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.complaintFilter.name}</TableCell>}
                                {this.props.columns.complaintCategoryFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.complaintCategoryFilter.name}</TableCell>}
                                {this.props.columns.vulnerabilityCategoryFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.vulnerabilityCategoryFilter.name}</TableCell>}
                                {this.props.columns.vulnerabilityFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.vulnerabilityFilter.name}</TableCell>}
                                {this.props.columns.scoreFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.scoreFilter.name}</TableCell>}
                                {this.props.columns.saleTypeFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.saleTypeFilter.name}</TableCell>}
                                {this.props.columns.productFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.productFilter.name}</TableCell>}
                                {this.props.columns.productTypeFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.productTypeFilter.name}</TableCell>}
                                {this.props.columns.productOfferFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.productOfferFilter.name}</TableCell>}
                                {this.props.columns.clientReferenceFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.clientReferenceFilter.name}</TableCell>}
                                {this.props.columns.themeFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.themeFilter.name}</TableCell>}
                                {this.props.columns.originatorFilters.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.originatorFilters.name}</TableCell>}
                                {this.props.columns.outcomeFilters.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.outcomeFilters.name}</TableCell>}
                                {!this.props.monitoring && this.props.columns.customerIDFilter.isActive && <TableCell sx={styles.tableCell}>{this.props.columns.customerIDFilter.name}</TableCell>}
                                {this.props.isRTM ? <TableCell sx={styles.tableCell} /> : <Fragment />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows}
                        </TableBody>
                    </Table>
                </TableContainer>
            );

        }

        private openCallInfo = (e) => {
            if (e.target.tagName !== 'BUTTON' && e.target.tagName !== 'SPAN') {
                const syspk = e.currentTarget.getAttribute('data-key');

                this.props.openMonitoring(syspk);
            }
        }
    }
);

export default (DecoratedRecordingTable);
